// NodeSecurity.js

// Node Types Cache
import { getResolvedFields } from './NodeType';

export const PermissionAction = {
	VIEW: "VIEW",
	CONTRIBUTE: "CONTRIBUTE",
	EDIT: "EDIT",
	DELETE: "DELETE",
	COMMENT: "COMMENT",
	SHARE: "SHARE",
};

function resolveWorkspaceRole(node, user) {
	if (!node || !user?.key) return null;

	if (node?.workspaceRoleByUser && typeof node.workspaceRoleByUser === 'object') {
		const candidate = node.workspaceRoleByUser[user.key];
		if (typeof candidate === 'string' && candidate.trim()) return candidate.trim().toLowerCase();
	}

	const directRole = node?.workspaceRole || node?.memberRole || user?.workspaceRole || user?.data_profile?.workspaceRole;
	if (typeof directRole === 'string' && directRole.trim()) return directRole.trim().toLowerCase();

	return null;
}

/**
 * Determines whether a space user can perform an action on a node.
 *
 * RBAC policy:
 * - owner/admin can perform all actions
 * - member can view/contribute/comment/share
 * - only owner/admin can edit/delete (unless node owner)
 *
 * @param {string} action - One of PermissionAction (e.g., 'EDIT', 'VIEW', etc.)
 * @param {object} node - The node
 * @param {object} user - The current user's profile node
 * @param {object} [parentNode] - Legacy arg retained for callsite compatibility
 * @returns {boolean}
 */
export const canUser = (action, node, user, parentNode = null) => {
	void parentNode;

	if (!node || !user) return false;

	// Owners can always perform actions.
	if (node.userKey === user.key) return true;

	// USER profiles remain readable for app bootstrap/login paths.
	if (action === PermissionAction.VIEW && node.type === 'USER') return true;

	const workspaceRole = resolveWorkspaceRole(node, user);
	const isWorkspaceAdminOrOwner = workspaceRole === 'owner' || workspaceRole === 'admin';

	if (isWorkspaceAdminOrOwner) {
		return true;
	}

	if (!user?.key) return false;

	switch (action) {
		case PermissionAction.VIEW:
		case PermissionAction.CONTRIBUTE:
		case PermissionAction.COMMENT:
		case PermissionAction.SHARE:
			return true;

		case PermissionAction.EDIT:
		case PermissionAction.DELETE:
			return false;

		default:
			return false;
	}
};

/**
 * Legacy no-op normalizer retained for writer compatibility.
 */
export function normalizeNodeForWrite(node) {
	return node;
}

/**
 * Splits schema-based data fields into public vs secured payloads.
 *
 * - Non-secured fields remain on the root node document (data_*).
 * - Secured fields are moved into a separate object intended for the
 *   `_nodes/{nodeId}/secured/main` subcollection document.
 *
 * This relies on the `secured` boolean flag on nodetype schema fields.
 *
 * @param {object} node - The node object about to be written.
 * @returns {{ publicNode: Object, securedDoc: Object }}
 */
export const splitNodeSecuredFields = (node) => {
	if (!node || !node.type) {
		return { publicNode: node, securedDoc: {} };
	}

	const typeKey = node.type;
	const fields = getResolvedFields(typeKey, { includeHidden: true });
	const publicNode = { ...node };
	const securedDoc = {};

	const ensureSection = (target, rootKey) => {
		if (!Object.prototype.hasOwnProperty.call(target, rootKey) || typeof target[rootKey] !== 'object' || target[rootKey] === null) {
			target[rootKey] = {};
		}
		return target[rootKey];
	};

	if (Array.isArray(fields) && fields.length > 0) {
		fields.forEach((field) => {
			if (!field || !field.name || !field.type) return;
			if (!field.secured) return;

			const sourceKey = String(field.source || typeKey).trim().toLowerCase();
			const isFormField = typeof field.type === 'string' && field.type.endsWith('_form');

			// Determine where this field is stored on the node
			const rootKey = `data_${sourceKey}`;

			if (isFormField) {
				// Entire form section is considered secured (e.g. data_service)
				const sectionValue = publicNode[rootKey];
				if (sectionValue === undefined) return;

				securedDoc[rootKey] = sectionValue;
				// Remove from public node
				publicNode[rootKey] = null;
				return;
			}

			const section = publicNode[rootKey];
			if (!section || typeof section !== 'object') return;
			if (!Object.prototype.hasOwnProperty.call(section, field.name)) return;

			const value = section[field.name];

			// Write into secured doc under the same rootKey/name
			const securedSection = ensureSection(securedDoc, rootKey);
			securedSection[field.name] = value;

			// Remove from public node section
			const nextSection = { ...section };
			delete nextSection[field.name];
			publicNode[rootKey] = nextSection;
		});
	}

	return { publicNode, securedDoc };
};

